// src/documents/xlsx/buildXlsxOleBlob.js
//
// Bir xlsx ArrayBuffer'ini alip DXF OLE2FRAME entity'sinin binary blob'una
// cevirir. Cikti yapisi: 124-128 byte AutoCAD position+metadata header
// + CFB compound document (Root Entry / Ole / Package / CompObj).
//
// Strateji: docs/yukleme cetveli-1.dxf'ten cikarilan calisan OLE blob'unu
// (public/templates/yuklemeCetveliOleTemplate.bin) template olarak kullan;
// icindeki Package stream'in xlsx icerigini yeni xlsx ile degistir, eski
// thumbnail (OlePres000) sil. Header bytelarini aynen koru — AutoCAD bu
// CFB yapisini ve CompObj/CLSID'yi tanir.

import CFB from 'cfb';
import { loadOleTemplate } from './oleTemplateLoader';

/**
 * imageData'nın [y0, y0+stripH) satırlarından 24-bit TOP-DOWN packed DIB üretir
 * (BITMAPINFOHEADER negatif yükseklik + BGR bits). Şerit StretchDIBits için kaynak.
 */
function buildStripDibTopDown(data, width, fullW, y0, stripH) {
  const rowStride = (width * 3 + 3) & ~3;
  const dib = new Uint8Array(40 + rowStride * stripH);
  const dv = new DataView(dib.buffer);
  dv.setUint32(0, 40, true);
  dv.setInt32(4, width, true);
  dv.setInt32(8, -stripH, true);     // negatif = top-down
  dv.setUint16(12, 1, true);
  dv.setUint16(14, 24, true);
  dv.setUint32(16, 0, true);          // BI_RGB
  dv.setUint32(20, rowStride * stripH, true);
  dv.setInt32(24, 2835, true);
  dv.setInt32(28, 2835, true);
  dv.setUint32(32, 0, true);
  dv.setUint32(36, 0, true);
  for (let yy = 0; yy < stripH; yy++) {
    const srcRow = y0 + yy;          // top-down: ilk satır = üst
    let p = 40 + yy * rowStride;
    for (let x = 0; x < width; x++) {
      const si = (srcRow * fullW + x) * 4;
      dib[p++] = data[si + 2]; // B
      dib[p++] = data[si + 1]; // G
      dib[p++] = data[si];     // R
    }
  }
  return dib;
}

/**
 * Bitmap'i KÜÇÜK StretchDIBits şeritlerine bölen bir WMF üretir. AutoCAD WMF OLE
 * presentation'ında tek dev record'u tutarsız render ediyordu; template gibi küçük
 * record'lar (≤~32KB) güvenilir. Yapı: METAHEADER + SetMapMode(ANISOTROPIC) +
 * SetWindowExt + SetWindowOrg + N×StretchDIBits(şerit) + EOF.
 */
function buildTiledWmf(imageData) {
  const { data, width, height } = imageData;
  const rowStride = (width * 3 + 3) & ~3;
  // Şerit yüksekliği: her record ~32KB hedefle (en az 1 satır).
  const STRIP = Math.max(1, Math.floor(32000 / rowStride));

  const recs = [];
  const simpleRec = (func, params) => {
    const size = 3 + params.length;
    const r = new Uint8Array(size * 2);
    const dv = new DataView(r.buffer);
    dv.setUint32(0, size, true);
    dv.setUint16(4, func, true);
    params.forEach((p, i) => dv.setUint16(6 + i * 2, p & 0xFFFF, true));
    return r;
  };
  recs.push(simpleRec(0x0103, [8]));            // SetMapMode MM_ANISOTROPIC
  recs.push(simpleRec(0x020C, [height, width])); // SetWindowExt (y,x)
  recs.push(simpleRec(0x020B, [0, 0]));          // SetWindowOrg (y,x)

  for (let y0 = 0; y0 < height; y0 += STRIP) {
    const sh = Math.min(STRIP, height - y0);
    let dib = buildStripDibTopDown(data, width, width, y0, sh);
    if (dib.length & 1) { const d = new Uint8Array(dib.length + 1); d.set(dib); dib = d; }
    const paramWords = 2 + 9 + dib.length / 2; // rop(2w) + 9 param + dib
    const size = 3 + paramWords;
    const r = new Uint8Array(size * 2);
    const dv = new DataView(r.buffer);
    let o = 0;
    dv.setUint32(o, size, true); o += 4;
    dv.setUint16(o, 0x0F43, true); o += 2;        // META_STRETCHDIBITS
    dv.setUint32(o, 0x00CC0020, true); o += 4;    // SRCCOPY
    dv.setUint16(o, 0, true); o += 2;             // DIB_RGB_COLORS
    dv.setUint16(o, sh, true); o += 2;            // SrcHeight
    dv.setUint16(o, width, true); o += 2;         // SrcWidth
    dv.setUint16(o, 0, true); o += 2;             // ySrc
    dv.setUint16(o, 0, true); o += 2;             // xSrc
    dv.setUint16(o, sh, true); o += 2;            // DestHeight
    dv.setUint16(o, width, true); o += 2;         // DestWidth
    dv.setUint16(o, y0, true); o += 2;            // yDest (şeridin üst konumu)
    dv.setUint16(o, 0, true); o += 2;             // xDest
    r.set(dib, o);
    recs.push(r);
  }
  recs.push(simpleRec(0x0000, []));               // EOF

  const bodyLen = recs.reduce((s, r) => s + r.length, 0);
  const maxRecord = Math.max(...recs.map(r => r.length / 2));
  const totalWords = 9 + bodyLen / 2;

  const wmf = new Uint8Array(18 + bodyLen);
  const dv = new DataView(wmf.buffer);
  dv.setUint16(0, 1, true);
  dv.setUint16(2, 9, true);
  dv.setUint16(4, 0x0300, true);
  dv.setUint32(6, totalWords, true);
  dv.setUint16(10, 0, true);
  dv.setUint32(12, maxRecord, true);
  dv.setUint16(16, 0, true);
  let off = 18;
  for (const r of recs) { wmf.set(r, off); off += r.length; }
  return wmf;
}

/**
 * OLE2FRAME presentation (OlePres000) stream'i üretir — CF_ENHMETAFILE (14,
 * template ile aynı format alanı), içinde DIB'i çizen WMF metafile. AutoCAD bu
 * cache'lenmiş metafile presentation'ı gösterir.
 * Yapı: 40-byte OLEPresentationStream header + WMF + 8-byte TOC trailer.
 */
function buildDibPresentationStream(imageData) {
  const { width, height } = imageData;
  const wmf = buildTiledWmf(imageData);

  const himetricW = Math.round((width / 96) * 2540);
  const himetricH = Math.round((height / 96) * 2540);
  const headerSize = 40;
  const TRAILER_SIZE = 8;
  const stream = new Uint8Array(headerSize + wmf.length + TRAILER_SIZE);
  const sv = new DataView(stream.buffer);
  let h = 0;
  sv.setUint32(h, 0xFFFFFFFF, true); h += 4;   // marker
  sv.setUint32(h, 14, true); h += 4;           // format alanı (template ile aynı)
  sv.setUint32(h, 4, true); h += 4;            // TargetDeviceSize = 4 (yok)
  sv.setUint32(h, 1, true); h += 4;            // DVASPECT_CONTENT
  sv.setUint32(h, 0xFFFFFFFF, true); h += 4;   // Lindex
  sv.setUint32(h, 2, true); h += 4;            // Advf
  sv.setUint32(h, 0, true); h += 4;            // Reserved1
  sv.setUint32(h, himetricW, true); h += 4;
  sv.setUint32(h, himetricH, true); h += 4;
  sv.setUint32(h, wmf.length, true); h += 4;   // Size (presentation data)
  stream.set(wmf, headerSize);
  sv.setUint32(headerSize + wmf.length, 0x494E414E, true);     // TocSignature "NANI"
  sv.setUint32(headerSize + wmf.length + 4, 0x00000000, true); // TocCount = 0
  return stream;
}

/**
 * @param {ArrayBuffer|Uint8Array} xlsxBytes
 * @param {{data, width, height}} [presentationImageData] - verilirse OlePres000
 *   stream'i bu görüntünün DIB presentation'ı ile DEĞİŞTİRİLİR (AutoCAD bunu
 *   gösterir). Verilmezse eski thumbnail silinir (geri uyum / fallback).
 * @returns {Promise<Uint8Array>} OLE2FRAME binary blob (header + CFB)
 */
export async function buildXlsxOleBlob(xlsxBytes, presentationImageData = null) {
  const templateBlob = await loadOleTemplate();
  const tpl = templateBlob instanceof Uint8Array ? templateBlob : new Uint8Array(templateBlob);

  // CFB magic offset
  let cfbOff = -1;
  for (let i = 0; i <= tpl.length - 8; i++) {
    if (tpl[i] === 0xD0 && tpl[i+1] === 0xCF && tpl[i+2] === 0x11 && tpl[i+3] === 0xE0 &&
        tpl[i+4] === 0xA1 && tpl[i+5] === 0xB1) { cfbOff = i; break; }
  }
  if (cfbOff === -1) throw new Error('OLE template: CFB magic bulunamadi');

  const cfb = CFB.read(tpl.slice(cfbOff), { type: 'array' });

  // Package stream'i bul ve icerigini yeni xlsx ile degistir
  const xlsxU8 = xlsxBytes instanceof Uint8Array
    ? xlsxBytes
    : new Uint8Array(xlsxBytes instanceof ArrayBuffer ? xlsxBytes : xlsxBytes.buffer || xlsxBytes);

  const pkgEntry = CFB.find(cfb, '/Package');
  if (!pkgEntry) throw new Error('OLE template: /Package stream bulunamadi');

  pkgEntry.content = xlsxU8;
  pkgEntry.size    = xlsxU8.length;

  if (presentationImageData && presentationImageData.data && presentationImageData.width) {
    // OlePres000'i taze DIB presentation ile DEĞİŞTİR → AutoCAD bunu gösterir.
    // (Eski kod siliyordu; presentation olmayınca OLE boş görünüyordu.)
    const presStream = buildDibPresentationStream(presentationImageData);
    const presEntry = Array.isArray(cfb.FileIndex)
      ? cfb.FileIndex.find(e => e?.name && /OlePres\d+/i.test(e.name))
      : null;
    if (presEntry) {
      presEntry.content = presStream;
      presEntry.size    = presStream.length;
    } else {
      // Template'te OlePres yoksa ekle.
      CFB.utils.cfb_add(cfb, '/\x02OlePres000', presStream);
    }
  } else {
    // Presentation yok → eski thumbnail'i kaldir (geri uyum / fallback).
    if (Array.isArray(cfb.FileIndex)) {
      cfb.FileIndex = cfb.FileIndex.filter(e => !e?.name || !/OlePres\d+/i.test(e.name));
    }
    if (Array.isArray(cfb.FullPaths)) {
      cfb.FullPaths = cfb.FullPaths.filter(p => !p || !/OlePres\d+/i.test(p));
    }
  }

  const newCfbRaw = CFB.write(cfb, { type: 'array' });
  const newCfb = newCfbRaw instanceof Uint8Array ? newCfbRaw : new Uint8Array(newCfbRaw);

  const out = new Uint8Array(cfbOff + newCfb.length);
  out.set(tpl.slice(0, cfbOff), 0);
  out.set(newCfb, cfbOff);
  return out;
}

export default buildXlsxOleBlob;
